import { TrendingUp, ArrowUpRight, ArrowDownRight, Minus, Eye, MapPin, Search, Filter, Archive, Loader2, ChevronRight, BarChart3, User, Info} from "lucide-react";
import { useState, useMemo, Fragment, type ReactElement } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Avatar, AvatarImage, AvatarFallback } from "@/app/_module/components/ui/avatar";
import { getInitialsFromFullName } from "@/app/_module/utils/get-initials";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getAdminAccess } from "@/app/_module/utils/admin-access";
import type { SubAgency, Agency } from "../../types/types";
import { useUpdateAgency } from "../../hooks/use-hook";

import CreateSubAgencyModal from "./CreateSubAgencyModal";
import { TransferAgenciesModal } from "./TransferAgenciesModal";
import EditAgencyModal from "./editAgency";
import { useToast } from "@/app/_module/hooks/use-toast";
import { retireAgency } from "../../services/transfer-agencies-services";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import type { SubAgencyTreeNode } from "../../services/services";
import { getAgencyUsers } from "../../services/services";
import type { AgencyUserWithAgency } from "../../types/types";
import { AgencyDeepDivePanel } from "./AgencyDeepDivePanel";

// Presence pill config — mirrors the Agency Agents table so the two read the
// same. Keyed by userStatus, falling back to offline.
const AGENT_STATUS_CONFIG: Record<string, { dot: string; badge: string; label: string }> = {
  available: { dot: 'bg-green-500 animate-pulse', badge: 'bg-green-100 text-green-800 border-green-300', label: 'Online' },
  idle: { dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Idle' },
  on_call: { dot: 'bg-blue-500 animate-pulse', badge: 'bg-blue-100 text-blue-800 border-blue-300', label: 'On Call' },
  dnd: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-800 border-red-300', label: 'Do Not Disturb' },
  offline: { dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600 border-gray-300', label: 'Offline' },
};

const formatAgentRole = (role: string): string => {
  const map: Record<string, string> = { owner: 'Owner', admin: 'Admin', agent: 'Agent', viewer: 'Viewer' };
  return map[role?.toLowerCase()] || role;
};

// Growth = production vs the previous window of the same length; null when that window had
// nothing to compare with. Arrow, color and wording all follow the sign — the old cell always
// drew an up arrow, so a drop read "↗ -100.0%".
function GrowthChip({ pct, current }: { pct: number | null | undefined; current: number }) {
  const chip = "inline-flex items-center gap-0.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums";
  if (pct == null) {
    return current > 0 ? (
      <span className={`${chip} bg-emerald-50 text-emerald-700`} title="Nothing in the previous period to compare with">
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        from $0
      </span>
    ) : (
      <span className="text-muted-foreground" title="No production in this period or the one before">—</span>
    );
  }
  if (pct === 0) {
    return (
      <span className={`${chip} bg-gray-100 text-gray-600`} title="Same production as the previous period">
        <Minus className="h-3.5 w-3.5" aria-hidden="true" />
        0%
      </span>
    );
  }
  const up = pct > 0;
  const abs = Math.abs(pct);
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`${chip} ${up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
      title={`${up ? "Up" : "Down"} ${abs.toFixed(1)}% vs the previous period`}
    >
      <Arrow className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="sr-only">{up ? "Up" : "Down"} </span>
      {!up && current <= 0 ? "to $0" : `${abs >= 10 ? Math.round(abs).toLocaleString() : abs.toFixed(1)}%`}
    </span>
  );
}

interface SubAgenciesTableProps {
  subAgencies: SubAgency[];
  onViewDetails: (subAgency: SubAgency) => void;
  isLoading?: boolean;
  parentAgencyId?: string; // When provided, scopes to this parent instead of user's current agency
  parentAgencyName?: string; // Display name for the parent (used in CreateSubAgencyModal banner)
  treeNodes?: SubAgencyTreeNode[]; // Full descendant tree with subtree-rolled-up stats
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string; // Custom date range (overrides period when both set)
  endDate?: string;
  onViewAgentDetails?: (agencyUser: AgencyUserWithAgency) => void; // Opens the agent detail view
}

export const SubAgenciesTable = function ({
  subAgencies,
  onViewDetails,
  isLoading = false,
  parentAgencyId: explicitParentId,
  parentAgencyName: explicitParentName,
  treeNodes = [],
  period = '6-months',
  startDate,
  endDate,
  onViewAgentDetails,
}: SubAgenciesTableProps) {
  const { user } = useUserContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [selectedAgencyIds, setSelectedAgencyIds] = useState<Set<string>>(new Set());

  // Shell: header ⓘ tooltips render un-portalled inside the horizontal
  // scroller and get clipped — tap-to-toggle fixed panel instead (same
  // pattern as CarrierBreakdown / leaderboard). Desktop keeps Radix hover.
  const isShell =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  const [helpTip, setHelpTip] = useState<{ top: number; left: number; text: string } | null>(null);
  const showHelp = (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    if (helpTip) {
      setHelpTip(null);
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 240;
    const left = Math.min(
      Math.max(8, r.left + r.width / 2 - width / 2),
      window.innerWidth - width - 8,
    );
    setHelpTip({ top: r.bottom + 8, left, text });
  };
  const HeaderInfo = ({ text }: { text: string }) =>
    isShell ? (
      <button
        type="button"
        aria-label="Info"
        onClick={(e) => showHelp(e, text)}
        className="inline-flex items-center align-middle"
      >
        <Info className="h-3.5 w-3.5 text-primary/70" />
      </button>
    ) : (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label="Info" className="inline-flex cursor-help items-center">
              <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64 text-xs">
            {text}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  const updateAgencyMutation = useUpdateAgency();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [retiringId, setRetiringId] = useState<string | null>(null);
  const [retireTarget, setRetireTarget] = useState<{ id: string; name: string } | null>(null);
  // Expanding a row reveals its SUB-AGENCIES (the tree) only. Agents are behind
  // a separate "Show agents" button (agentsOpenRows) so the two concerns don't
  // clutter each other.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [agentsOpenRows, setAgentsOpenRows] = useState<Set<string>>(new Set());
  const [pieOpenId, setPieOpenId] = useState<string | null>(null);
  // Shell: the deep-dive panel renders BELOW the table instead of as an
  // in-table row (sticky inside a td is unreliable in WKWebView, so the row
  // opened half off-screen). Panel props are stashed at toggle time so the
  // below-table render needs no tree lookup.
  const [piePanelProps, setPiePanelProps] = useState<Record<string, unknown> | null>(null);
  const togglePie = (id: string, panelProps?: Record<string, unknown>) =>
    setPieOpenId((p) => {
      const next = p === id ? null : id;
      setPiePanelProps(next ? panelProps ?? null : null);
      return next;
    });
  // Node/roll shapes are identical apart from id+name living on the agency
  // row at top level — callers merge those in.
  const buildPieProps = (n: any): Record<string, unknown> => ({
    agencyId: n.id,
    agencyName: n.name,
    period,
    startDate,
    endDate,
    ownPremium: n.directPremium,
    ownIssuedPremium: n.directIssuedPremium || 0,
    slices: (childrenOf.get(n.id) || []).map((c) => ({
      id: c.id,
      name: c.name,
      premium: c.totalPremium,
      issuedPremium: c.issuedPremium || 0,
      policies: c.totalPolicies,
      agents: c.totalAgents,
      averagePolicySize: c.averagePolicySize,
      closingRatio: c.closingRatio,
    })),
    totalPremium: n.totalPremium,
    issuedPremium: n.issuedPremium || 0,
    totalPolicies: n.totalPolicies,
    totalAgents: n.totalAgents,
    closingRatio: n.closingRatio,
    averagePolicySize: n.averagePolicySize,
  });
  // Agents WITHIN each agency (this agency + all its sub-agencies, in one call),
  // lazy-loaded the first time they're shown. Same shape as the Agency Agents
  // table so the rows can show role + presence + a View action.
  const [agentsByAgency, setAgentsByAgency] = useState<Map<string, AgencyUserWithAgency[]>>(new Map());
  const [loadingAgentsFor, setLoadingAgentsFor] = useState<Set<string>>(new Set());
  const loadAgents = async (agencyId: string) => {
    if (agentsByAgency.has(agencyId) || loadingAgentsFor.has(agencyId)) return;
    setLoadingAgentsFor((prev) => new Set(prev).add(agencyId));
    try {
      // DIRECT members only — each agency row lists its own people. Fetching
      // the whole subtree here duplicated everyone: Selina showed under BOTH
      // wompwompTEST (parent) and Hitmanz Inc (her own agency) when both rows
      // were expanded. Descendants' people live under their own rows.
      const res: any = await getAgencyUsers(agencyId);
      const list: AgencyUserWithAgency[] = res?.users ?? res?.data ?? [];
      // The backend already strips UPLINE (ancestor) owners from this list —
      // the people shown are the agency's own team. Do NOT also drop role
      // 'owner' here: that hid each shop's LOCAL owner, so an agency whose
      // whole team holds owner seats (or a solo owner-run shop) listed nobody
      // while the count chip said otherwise.
      const agentsOnly = list.filter((u) => u?.user);
      setAgentsByAgency((prev) => new Map(prev).set(agencyId, agentsOnly));
    } catch {
      setAgentsByAgency((prev) => new Map(prev).set(agencyId, []));
    } finally {
      setLoadingAgentsFor((prev) => {
        const n = new Set(prev);
        n.delete(agencyId);
        return n;
      });
    }
  };

  // Rollup lookup + tree structure from the subtree-stats endpoint. Each row's
  // premium / policies / agents come from here (combined across every level
  // beneath it); childrenOf drives the expand.
  const rollupById = useMemo(() => {
    const m = new Map<string, SubAgencyTreeNode>();
    for (const n of treeNodes) m.set(n.id, n);
    return m;
  }, [treeNodes]);
  const childrenOf = useMemo(() => {
    const m = new Map<string, SubAgencyTreeNode[]>();
    for (const n of treeNodes) {
      const p = n.parentAgencyId || "";
      if (!m.has(p)) m.set(p, []);
      m.get(p)!.push(n);
    }
    for (const arr of m.values()) arr.sort((a, b) => b.totalPremium - a.totalPremium);
    return m;
  }, [treeNodes]);
  // Expand / collapse an agency's SUB-AGENCIES (does not touch agents).
  const toggleExpand = (id: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Show / hide the AGENTS within an agency (lazy-loads them the first time).
  const toggleAgents = (id: string) =>
    setAgentsOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        void loadAgents(id);
      }
      return next;
    });

  // Render the individual AGENTS within an agency's subtree (people), lazy-
  // loaded. Mirrors the Agency Agents table: avatar, name, role, live presence
  // pill, and a View action that opens the agent detail view.
  const renderAgentRows = (agencyId: string, depth: number): ReactElement[] => {
    const pad = (depth + 1) * 18 + 28;
    const agents = agentsByAgency.get(agencyId);
    if (!agents) {
      if (loadingAgentsFor.has(agencyId)) {
        return [
          <TableRow key={`${agencyId}-agents-loading`} className="bg-primary/5">
            <TableCell colSpan={10}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground" style={{ paddingLeft: pad }}>
                <Loader2 className="h-3 w-3 animate-spin" /> Loading agents…
              </div>
            </TableCell>
          </TableRow>,
        ];
      }
      return [];
    }
    if (agents.length === 0) {
      return [
        <TableRow key={`${agencyId}-agents-empty`} className="bg-primary/5">
          <TableCell colSpan={10}>
            <div className="text-xs text-muted-foreground" style={{ paddingLeft: pad }}>No agents in this agency.</div>
          </TableCell>
        </TableRow>,
      ];
    }
    return agents.map((au) => {
      const u = au.user as any;
      const status = u.userStatus || (u.isOnline ? 'available' : 'offline');
      const cfg = AGENT_STATUS_CONFIG[status] || AGENT_STATUS_CONFIG.offline;
      const agencyName = (au as any).agency?.name as string | undefined;
      return (
        <TableRow key={`agent-${agencyId}-${au.id}`} className="bg-primary/5 hover:bg-primary/10">
          <TableCell colSpan={10} className="py-2">
            <div className="subag-agent-row flex items-center gap-3" style={{ paddingLeft: pad }}>
              <Avatar className="h-8 w-8 flex-shrink-0 border border-primary/20">
                <AvatarImage src={u.profileImageUrl || undefined} alt={u.name} className="object-cover" />
                <AvatarFallback className="bg-primary text-[10px] font-semibold text-primary-foreground">
                  {getInitialsFromFullName(u.name?.split(' ')[0] || 'U', u.name?.split(' ')[1] || 'N')}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">{u.name}</span>
                  <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-normal">{formatAgentRole(au.role)}</Badge>
                  {agencyName && <span className="truncate text-[11px] text-muted-foreground">· {agencyName}</span>}
                </div>
                <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
              </div>
              <Badge variant="outline" className={`${cfg.badge} inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap`}>
                <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} /> {cfg.label}
              </Badge>
              {onViewAgentDetails && (
                <Button size="sm" variant="outline" className="h-7 flex-shrink-0 gap-1 px-2 text-xs" onClick={() => onViewAgentDetails(au)}>
                  <Eye className="h-3 w-3" /> View
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  // Non-owner agent count for a sub-agency (owners are auto-assigned parent
  // owners, not "in" the agency). `?? ` so an explicit 0 is respected.
  const agentCountOf = (agency: any): number =>
    (agency.stats?.agentCount as number) ??
    (agency.agencyUsers?.filter((au: any) => au.role?.toLowerCase() !== "owner").length as number) ??
    0;

  // Retire an empty sub-agency (folds it into its parent + soft-deletes it).
  // Opens an in-app confirm dialog rather than the browser's window.confirm.
  const requestRetire = (agency: { id: string; name: string }) => {
    if (retiringId) return;
    setRetireTarget(agency);
  };
  const confirmRetire = async () => {
    if (!retireTarget || retiringId) return;
    const agency = retireTarget;
    setRetiringId(agency.id);
    try {
      await retireAgency(agency.id);
      toast({ title: "Sub-agency retired", description: `"${agency.name}" was removed.` });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["agencies", "subAgencies"], exact: false }),
        queryClient.invalidateQueries({ queryKey: ["agencies", "hierarchy"], exact: false }),
      ]);
      setRetireTarget(null);
    } catch (err: any) {
      toast({ title: "Could not retire", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      setRetiringId(null);
    }
  };

  // Render the expanded descendant rows for a parent agency (recursive, indented).
  // Nested rows show the same subtree-rolled-up numbers as the top level —
  // premium, policies (+ growth %), closing ratio and agents — plus Edit/View.
  const renderNestedRows = (parentId: string, depth: number): ReactElement[] => {
    const rows: ReactElement[] = [];
    for (const node of childrenOf.get(parentId) || []) {
      const hasKids = (childrenOf.get(node.id) || []).length > 0;
      const isOpen = expandedRows.has(node.id);
      const agentsOpen = agentsOpenRows.has(node.id);
      const isEmpty = node.totalAgents === 0 && !hasKids;
      // Chevron reveals sub-agencies only; agents are a separate button.
      const canExpand = hasKids;
      rows.push(
        <TableRow key={node.id} className="bg-muted/20">
          <TableCell className="w-8" />
          <TableCell className="font-medium">
            <div className="flex items-center gap-1" style={{ paddingLeft: (depth + 1) * 18 }}>
              <button
                type="button"
                onClick={() => canExpand && toggleExpand(node.id)}
                className={`flex h-4 w-4 flex-shrink-0 items-center justify-center ${canExpand ? "text-gray-500 hover:text-gray-900" : "text-transparent"}`}
                aria-label={canExpand ? (isOpen ? "Collapse" : "Expand") : undefined}
              >
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </button>
              <span className="truncate">{node.name}</span>
            </div>
          </TableCell>
          <TableCell className="text-muted-foreground">
            <div className="flex items-center gap-1"><User className="h-3 w-3" /><span>{(node as any).ownerName || "—"}</span></div>
          </TableCell>
          <TableCell>
            <div className="flex flex-col">
              <span>${Math.round(node.totalPremium || 0).toLocaleString()}</span>
              <span className="text-xs text-muted-foreground">Avg: ${Math.round(node.averagePolicySize || 0).toLocaleString()}</span>
            </div>
          </TableCell>
          {/* Nested rows are rendered by this separate function, so the Total
              Issued cell has to be added here too — without it the expanded
              sub-agency rows were one column short and left the value blank. */}
          <TableCell>
            <span className="font-medium text-emerald-600">
              ${Math.round(node.issuedPremium || 0).toLocaleString()}
            </span>
          </TableCell>
          <TableCell>{(node.totalPolicies || 0).toLocaleString()}</TableCell>
          <TableCell>{(node.closingRatio || 0).toFixed(1)}%</TableCell>
          <TableCell>
            <GrowthChip pct={node.changePercentage} current={node.totalPremium || 0} />
          </TableCell>
          <TableCell>
            <div className="flex flex-col items-start gap-0.5">
              <div className="flex items-center gap-1.5">
                {/* Chip = this agency's OWN people (expand lists exactly them). */}
                {node.directAgents > 0 ? (
                  <button
                    type="button"
                    onClick={() => toggleAgents(node.id)}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${agentsOpen ? "border-primary/30 bg-primary/10 text-primary" : "hover:bg-muted"}`}
                    title={agentsOpen ? "Hide this agency's people" : "Show this agency's people"}
                  >
                    <User className="h-3 w-3" />
                    {node.directAgents.toLocaleString()}
                    <ChevronRight className={`h-3 w-3 transition-transform ${agentsOpen ? "rotate-90" : ""}`} />
                  </button>
                ) : node.totalAgents > 0 ? (
                  <Badge variant="secondary" title="All people are in sub-agencies — expand them below">{node.totalAgents.toLocaleString()} in subs</Badge>
                ) : (
                  <Badge variant="secondary">0 Agents</Badge>
                )}
                {isEmpty && <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">Empty</Badge>}
              </div>
              {node.totalAgents > node.directAgents && node.directAgents > 0 && (
                <span className="text-[10px] text-muted-foreground whitespace-nowrap pl-0.5">+{(node.totalAgents - node.directAgents).toLocaleString()} in sub-agencies</span>
              )}
            </div>
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2">
              <EditAgencyModal
                defaultValues={nodeToAgency(node)}
                onEdit={(data, close) => handleEditSubAgency({ id: node.id } as SubAgency, data, close)}
              />
              <Button className="flex items-center gap-1" onClick={() => togglePie(node.id, buildPieProps(node))} size="sm" variant={pieOpenId === node.id ? "default" : "outline"} title="Deep dive">
                <BarChart3 className="h-3 w-3" /> View
              </Button>
              {isEmpty && (
                <Button className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => requestRetire(node)} disabled={retiringId === node.id} size="sm" variant="outline" title="Retire this empty sub-agency">
                  {retiringId === node.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />} Retire
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>,
      );
      if (!isShell && pieOpenId === node.id) {
        rows.push(
          <TableRow key={`${node.id}-pie`}>
            <TableCell colSpan={10} className="bg-muted/10 p-0">
              <div className="px-3 py-3">
                <AgencyDeepDivePanel
                  agencyId={node.id}
                  agencyName={node.name}
                  period={period}
                  startDate={startDate}
                  endDate={endDate}
                  ownPremium={node.directPremium}
                  ownIssuedPremium={node.directIssuedPremium || 0}
                  slices={(childrenOf.get(node.id) || []).map((c) => ({ id: c.id, name: c.name, premium: c.totalPremium, issuedPremium: c.issuedPremium || 0, policies: c.totalPolicies, agents: c.totalAgents, averagePolicySize: c.averagePolicySize, closingRatio: c.closingRatio }))}
                  totalPremium={node.totalPremium}
                  issuedPremium={node.issuedPremium || 0}
                  totalPolicies={node.totalPolicies}
                  totalAgents={node.totalAgents}
                  closingRatio={node.closingRatio}
                  averagePolicySize={node.averagePolicySize}
                  onViewAgentDetails={onViewAgentDetails}
                />
              </div>
            </TableCell>
          </TableRow>,
        );
      }
      if (agentsOpen) rows.push(...renderAgentRows(node.id, depth + 1));
      if (isOpen && hasKids) rows.push(...renderNestedRows(node.id, depth + 1));
    }
    return rows;
  };

  // Handle edit sub-agency - convert SubAgency to Agency format for EditAgencyModal
  const handleEditSubAgency = (subAgency: SubAgency, data: Agency, close: () => void) => {
    updateAgencyMutation.mutate(
      {
        agencyId: subAgency.id,
        data: {
          name: data.name,
          description: data.description,
          contactEmail: data.contactEmail,
          location: data.location,
          // Note: isActive is read-only, contact support to change
        },
      },
      {
        onSuccess: () => {
          close();
        },
      }
    );
  };

  // Convert a tree node to the Agency shape EditAgencyModal expects. Nodes only
  // carry id/name/location/isActive; the modal edits name/description/email/
  // location, and the save only uses the id, so the rest default safely.
  const nodeToAgency = (node: SubAgencyTreeNode): Agency => ({
    id: node.id,
    name: node.name,
    description: "",
    contactEmail: "",
    location: node.location || "Main Office",
    isActive: node.isActive,
    createdAt: "",
    updatedAt: "",
    deletedAt: null,
  });

  // Convert SubAgency to Agency format for EditAgencyModal
  const subAgencyToAgency = (subAgency: SubAgency): Agency => ({
    id: subAgency.id,
    name: subAgency.name,
    description: subAgency.description || "",
    contactEmail: subAgency.contactEmail || "",
    location: subAgency.location || "Main Office",
    isActive: subAgency.isActive,
    createdAt: subAgency.createdAt,
    updatedAt: subAgency.updatedAt,
    deletedAt: subAgency.deletedAt,
  });

  // Determine admin access level for proper scoping
  const agencyRole = user.currentAgency?.role;
  const adminAccess = getAdminAccess(user.user.userType, agencyRole);

  // Extract stable primitive value for dependencies
  const currentAgencyId = user.currentAgency?.agency?.id;

  // Filter sub-agencies based on admin access level
  const scopedSubAgencies = useMemo(() => {
    if (!subAgencies) return [];

    // When viewing nested sub-agencies (explicit parent provided),
    // only show direct children of that parent
    if (explicitParentId) {
      return subAgencies.filter(subAgency =>
        subAgency.parentAgencyId === explicitParentId
      );
    }

    // System Admin can see all sub-agencies
    if (adminAccess.canManageAllAgencies) {
      return subAgencies;
    }

    // Agency Owner/Admin can only see sub-agencies under their agency
    if (adminAccess.canManageOwnAgencies && currentAgencyId) {
      return subAgencies.filter(subAgency =>
        subAgency.parentAgencyId === currentAgencyId
      );
    }

    // Regular users cannot see sub-agencies
    return [];
  }, [subAgencies, adminAccess, currentAgencyId, explicitParentId]);

  // Filter and search sub-agencies with improved type safety
  const filteredSubAgencies = useMemo(() => {
    return scopedSubAgencies.filter((agency) => {
      // Search filter - improved type safety
      const matchesSearch = searchTerm === "" || 
        agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter with proper type safety
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "active" && agency.isActive) ||
        (statusFilter === "inactive" && !agency.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [scopedSubAgencies, searchTerm, statusFilter]);

  if (isLoading) {
    return (
      <Card className="border-none rounded-none shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Sub-Agencies
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading sub-agencies...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if user has permission to view sub-agencies
  if (!adminAccess.canManageOwnAgencies) {
    return (
      <Card className="border-none rounded-none shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Sub-Agencies
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">
              You don't have permission to view sub-agencies
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subAgencies || subAgencies.length === 0) {
    return (
      <Card className="border-none rounded-none shadow-none">
        <div className="subag-head flex justify-between items-center mb-4">
          <CardTitle className="flex gap-2">
            Sub-Agencies (0)
          </CardTitle>
          <div className="flex gap-2 justify-end">
            <CreateSubAgencyModal
              parentAgencyId={explicitParentId}
              parentAgencyName={explicitParentName}
              onSuccess={() => {
                // The hook will automatically invalidate and refetch
              }}
            />
          </div>
        </div>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">No sub-agencies found</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none rounded-none shadow-none">
      <div className="subag-head flex justify-between items-center mb-4">
        <CardTitle className="flex gap-2">
          Sub-Agencies ({filteredSubAgencies.length})
        </CardTitle>
        <div className="flex gap-2 justify-end">
          {(explicitParentId || currentAgencyId) && (
            <TransferAgenciesModal
              rootAgencyId={(explicitParentId || currentAgencyId) as string}
              rootAgencyName={explicitParentName || "your agency"}
              preselectedSourceIds={Array.from(selectedAgencyIds)}
              trigger={
                <Button variant={selectedAgencyIds.size > 0 ? "default" : "outline"} className="gap-2">
                  <TrendingUp className="h-4 w-4" />
                  {selectedAgencyIds.size > 0
                    ? `Transfer ${selectedAgencyIds.size} Selected`
                    : "Transfer Agencies"}
                </Button>
              }
            />
          )}
          <CreateSubAgencyModal
            parentAgencyId={explicitParentId}
            parentAgencyName={explicitParentName}
            onSuccess={() => {
              // The hook will automatically invalidate and refetch
            }}
          />
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-4 flex gap-4 items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            className="pl-10"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search sub-agencies..."
            value={searchTerm}
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select onValueChange={(value) => setStatusFilter(value as "all" | "active" | "inactive")} value={statusFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <CardContent>
        {/* Shell help panel — fixed so the table's overflow clip can't eat it */}
        {helpTip && (
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setHelpTip(null)} />
            <div
              className="fixed z-[9999] w-[240px] rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-xl"
              style={{ top: helpTip.top, left: helpTip.left }}
            >
              {helpTip.text}
            </div>
          </>
        )}
        <Table className="subag-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <input
                  type="checkbox"
                  aria-label="Select all sub-agencies"
                  checked={filteredSubAgencies.length > 0 && filteredSubAgencies.every((a) => selectedAgencyIds.has(a.id))}
                  onChange={(e) =>
                    setSelectedAgencyIds((prev) => {
                      const next = new Set(prev);
                      filteredSubAgencies.forEach((a) => (e.target.checked ? next.add(a.id) : next.delete(a.id)));
                      return next;
                    })
                  }
                />
              </TableHead>
              <TableHead>Agency Name</TableHead>
              <TableHead>Agency Owner</TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-1">
                  Total Production
                  <HeaderInfo text="This is your agency total reported production. Everything marked sold and approved as well as in underwriting." />
                </span>
              </TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-1">
                  Total Issued
                  <HeaderInfo text="This is your agency total reported net production. Everything marked with sold and approved strictly shows up here." />
                </span>
              </TableHead>
              <TableHead>Policies</TableHead>
              <TableHead>Closing Ratio</TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-1">
                  Growth
                  <HeaderInfo text={'Production compared with the previous period of the same length. "from $0" means that earlier period had none.'} />
                </span>
              </TableHead>
              <TableHead>Agents</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSubAgencies.length === 0 ? (
              <TableRow>
                <TableCell className="text-center py-8 text-muted-foreground" colSpan={10}>
                  {searchTerm || statusFilter !== "all"
                    ? "No sub-agencies match your filters"
                    : "No sub-agencies found"}
                </TableCell>
              </TableRow>
            ) : (
              filteredSubAgencies.map((agency) => {
                // Numbers come from the subtree rollup (combined across every
                // level beneath this agency); fall back to the flat stats until
                // the tree loads.
                const roll = rollupById.get(agency.id);
                const hasKids = (childrenOf.get(agency.id) || []).length > 0;
                const isOpen = expandedRows.has(agency.id);
                const agentsOpen = agentsOpenRows.has(agency.id);
                const premium = roll?.totalPremium ?? agency.stats?.totalPremium ?? 0;
                const issued = roll?.issuedPremium ?? (agency.stats as { issuedPremium?: number } | undefined)?.issuedPremium ?? 0;
                const policies = roll?.totalPolicies ?? agency.stats?.totalPolicies ?? 0;
                const agentsTotal = roll?.totalAgents ?? agentCountOf(agency);
                const agentsDirect = roll?.directAgents ?? agentCountOf(agency);
                const avg = roll?.averagePolicySize ?? agency.stats?.averagePolicySize ?? 0;
                const isEmpty = agentsTotal === 0 && !hasKids;
                // Chevron reveals sub-agencies only; agents are a separate button.
                const canExpand = hasKids;
                return (
                <Fragment key={agency.id}>
                <TableRow className="hover:bg-muted/50">
                  <TableCell className="w-8" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${agency.name}`}
                      checked={selectedAgencyIds.has(agency.id)}
                      onChange={(e) =>
                        setSelectedAgencyIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(agency.id);
                          else next.delete(agency.id);
                          return next;
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => canExpand && toggleExpand(agency.id)}
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center ${canExpand ? "text-gray-500 hover:text-gray-900" : "text-transparent"}`}
                        aria-label={canExpand ? (isOpen ? "Collapse" : "Expand") : undefined}
                      >
                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </button>
                      <span className="truncate">{agency.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{(agency as any).ownerName || '—'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>${Math.round(premium).toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">
                        Avg: ${Math.round(avg).toLocaleString()}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-emerald-600">
                      ${Math.round(issued).toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell>{policies.toLocaleString()}</TableCell>
                  <TableCell>
                    {(roll?.closingRatio ?? agency.stats?.closingRatio ?? 0).toFixed(1)}%
                  </TableCell>
                  <TableCell>
                    {/* The tree's growth matches the rolled-up production in this row;
                        stats.changePercentage only counts the agency's direct members. */}
                    <GrowthChip pct={roll ? roll.changePercentage : agency.stats?.changePercentage} current={premium} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-0.5">
                      <div className="flex items-center gap-1.5">
                        {/* Chip = this agency's OWN people (expand lists exactly
                            them); descendants' people live under their own rows. */}
                        {agentsDirect > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleAgents(agency.id)}
                            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${agentsOpen ? "border-primary/30 bg-primary/10 text-primary" : "hover:bg-muted"}`}
                            title={agentsOpen ? "Hide this agency's people" : "Show this agency's people"}
                          >
                            <User className="h-3 w-3" />
                            {agentsDirect.toLocaleString()}
                            <ChevronRight className={`h-3 w-3 transition-transform ${agentsOpen ? "rotate-90" : ""}`} />
                          </button>
                        ) : agentsTotal > 0 ? (
                          <Badge variant="secondary" title="All people are in sub-agencies — expand them below">{agentsTotal.toLocaleString()} in subs</Badge>
                        ) : (
                          <Badge variant="secondary">0 Agents</Badge>
                        )}
                        {isEmpty && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                            Empty
                          </Badge>
                        )}
                      </div>
                      {agentsTotal > agentsDirect && agentsDirect > 0 && (
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap pl-0.5">+{(agentsTotal - agentsDirect).toLocaleString()} in sub-agencies</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <EditAgencyModal
                        defaultValues={subAgencyToAgency(agency)}
                        onEdit={(data, close) => handleEditSubAgency(agency, data, close)}
                      />
                      <Button
                        className="flex items-center gap-1"
                        onClick={() =>
                          roll
                            ? togglePie(agency.id, buildPieProps({ ...roll, id: agency.id, name: agency.name }))
                            : onViewDetails(agency)
                        }
                        size="sm"
                        variant={roll && pieOpenId === agency.id ? "default" : "outline"}
                        title={roll ? "Deep dive" : "View"}
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                      {isEmpty && (
                        <Button
                          className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() => requestRetire(agency)}
                          disabled={retiringId === agency.id}
                          size="sm"
                          variant="outline"
                          title="Retire this empty sub-agency"
                        >
                          {retiringId === agency.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Archive className="h-3 w-3" />
                          )}
                          Retire
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {!isShell && roll && pieOpenId === agency.id && (
                  <TableRow>
                    <TableCell colSpan={10} className="bg-muted/10 p-0">
                      <div className="px-3 py-3">
                        <AgencyDeepDivePanel
                          agencyId={agency.id}
                          agencyName={agency.name}
                          period={period}
                          startDate={startDate}
                          endDate={endDate}
                          ownPremium={roll.directPremium}
                          ownIssuedPremium={roll.directIssuedPremium || 0}
                          slices={(childrenOf.get(agency.id) || []).map((c) => ({ id: c.id, name: c.name, premium: c.totalPremium, issuedPremium: c.issuedPremium || 0, policies: c.totalPolicies, agents: c.totalAgents, averagePolicySize: c.averagePolicySize, closingRatio: c.closingRatio }))}
                          totalPremium={roll.totalPremium}
                          issuedPremium={roll.issuedPremium || 0}
                          totalPolicies={roll.totalPolicies}
                          totalAgents={roll.totalAgents}
                          closingRatio={roll.closingRatio}
                          averagePolicySize={roll.averagePolicySize}
                          onViewAgentDetails={onViewAgentDetails}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {agentsOpen && renderAgentRows(agency.id, 0)}
                {isOpen && hasKids && renderNestedRows(agency.id, 0)}
                </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {/* Shell: deep-dive panel at natural screen width below the table */}
        {isShell && pieOpenId && piePanelProps && (
          <div className="subag-pie-panel mt-3 rounded-xl border bg-muted/10 p-3" data-no-ptr>
            <AgencyDeepDivePanel {...(piePanelProps as any)} onViewAgentDetails={onViewAgentDetails} />
          </div>
        )}
      </CardContent>

      {/* Retire confirmation — in-app dialog (replaces window.confirm). */}
      <Dialog open={!!retireTarget} onOpenChange={(open) => { if (!open && !retiringId) setRetireTarget(null); }}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <Archive className="h-5 w-5 text-red-600" />
              Retire {retireTarget?.name}?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This sub-agency is empty. Retiring it removes it and moves any leftover
              leads up to its parent agency. This can&apos;t be easily undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="px-6" onClick={() => setRetireTarget(null)} disabled={!!retiringId}>
              Cancel
            </Button>
            <Button variant="destructive" className="px-6" onClick={confirmRetire} disabled={!!retiringId}>
              {retiringId ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Archive className="mr-1 h-4 w-4" />}
              {retiringId ? "Retiring…" : "Retire"}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </Card>
  );
};